import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSElement,
  WSFlexBox,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import { IPayeeEngagementResponse } from "@wingspanhq/payments/dist/interfaces";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { FieldView } from "../../../shared/components/FieldView/FieldView";
import React from "react";
import { useMutationDeletePayeeEngagement } from "../../../query/payeeEngagements/mutations/useMutationDeletePayeeEngagement";
import { ErrorContextKey } from "../../../services/platform";

export interface DeleteEngagementModalProps {
  payeeId: string;
  payeeName: string;
  engagement: IPayeeEngagementResponse;
  onClose: () => void;
}

export const DeleteEngagementModal: React.FC<DeleteEngagementModalProps> = ({
  payeeId,
  payeeName,
  engagement,
  onClose
}) => {
  const { openSnackbar } = useWSSnackbar();
  const [deleteEngagement, deleteEngagementMeta] =
    useMutationDeletePayeeEngagement();

  const handleDelete = async () => {
    await deleteEngagement(
      {
        payeeId,
        engagementId: engagement.payerPayeeEngagementId
      },
      {
        onSuccess: () => {
          openSnackbar({
            theme: "dark",
            type: "success",
            message: "Assignment deleted"
          });
          onClose();
        }
      }
    );
  };

  return (
    <>
      <WSText.ParagraphSm mb="L" weight="book">
        Are you sure you want to delete this assignment?
      </WSText.ParagraphSm>
      <WSPanel>
        <WSFlexBox gap="XL">
          <FieldView
            label="Assignment name"
            value={engagement.engagementName}
          />
          <FieldView label="Contractor name" value={payeeName} />
        </WSFlexBox>
      </WSPanel>

      <WSAlert
        my="2XL"
        theme="warning"
        icon="alert"
        size="M"
        title="Deleting assignment"
      >
        Deletion is a permanent action, it cannot be restored.
      </WSAlert>

      <WSErrorMessage
        error={deleteEngagementMeta.error}
        mb="XL"
        contextKey={ErrorContextKey.Engagement}
      />

      <WSActions
        alignment="fill"
        sortButtons={false}
        buttons={[
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          },
          {
            label: "Delete assignment",
            loading: deleteEngagementMeta.isLoading,
            onAsyncClick: handleDelete,
            kind: "Primary",
            destructive: true
          }
        ]}
      />
    </>
  );
};

export const useDeleteEngagementModal = () => {
  return useWSModal(DeleteEngagementModal, {
    title: "Delete assignment",
    size: "M"
  });
};
